import { useMemo } from 'react';

import { ERPTypes } from '@work4all/models/lib/Classes/ERPTypes.entity';
import { FileEntity } from '@work4all/models/lib/Classes/FileEntity.entity';
import { Invoice } from '@work4all/models/lib/Classes/Invoice.entity';
import { ProductionContract } from '@work4all/models/lib/Classes/ProductionContract.entity';
import { RaViewModel } from '@work4all/models/lib/Classes/RaViewModel.entity';
import { ReViewModel } from '@work4all/models/lib/Classes/ReViewModel.entity';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { useEntityPreviewDataRequest } from '../../hooks/use-entity-preview-data';

interface UseErpPreviewDataProps {
  entity: Entities;
  ids: string[] | number[];
}

const invoiceFields: Invoice<EMode.query> = {
  xInvoicePreviewUrl: null,
  additionalAddress2: {
    businessPartner: {
      id: null,
      data: {
        customer: {
          id: null,
          name: null,
          number: null,
          invoiceForm: null,
          eInvoiceFormat: null,
        },
        supplier: {
          id: null,
          name: null,
          number: null,
        },
      },
    },
  },
};

export const useErpPreviewData = (props: UseErpPreviewDataProps) => {
  const { entity, ids } = props;
  const requestEntityData = useMemo(() => {
    if (entity === Entities.reViewModel) {
      const reData: ReViewModel<EMode.query> = {
        id: null,
        userId: null,
        invoiceDate: null,
        note: null,
        permitStatus: null,
        permitDate: null,
        paymentStatus: null,
        accountingStatus: null,
        releasedByUserId: null,
        releasedByUser: {
          id: null,
          displayName: null,
        },
        approvedByUser: {
          id: null,
          displayName: null,
        },
        blockedByUser: {
          id: null,
          displayName: null,
        },
        user: {
          id: null,
          displayName: null,
        },
        supplier: {
          id: null,
          name: null,
          mainContact: {
            id: null,
            displayName: null,
          },
        },
        receipts: [
          {
            id: null,
            parentId: null,
            fileInfos: {
              fileEntityFilename: null,
              downloadUrl: null,
              previewUrl: null,
              previewMimeType: null,
              downloadMimeType: null,
              fileServiceProviderInfos: {
                id: null,
                fileName: null,
                mimeType: null,
                fspUrl: null,
              },
            },
          },
        ],
        datevDate: null,
      };
      return reData;
    } else if (entity === Entities.raViewModel) {
      const raData: RaViewModel<EMode.query> = {
        id: null,
        note: null,
        invoiceDate: null,
        customer: {
          id: null,
          name: null,
          number: null,
        },
        invoice: {
          id: null,
          ...invoiceFields,
          archivePdf: [
            {
              id: null,
              mimeType: null,
              fileInfos: {
                fileEntityFilename: null,
                downloadUrl: null,
                previewUrl: null,
                previewMimeType: null,
                downloadMimeType: null,
                fileServiceProviderInfos: {
                  id: null,
                  fileName: null,
                  mimeType: null,
                  fspUrl: null,
                },
              },
            },
          ],
        },
      };
      return raData;
    } else if (entity === Entities.productionContract) {
      const productionContractData: ProductionContract<EMode.query> = {
        id: null,
        closedByUserId: null,
        appliedByUserId: null,
        contactId: null,
        articleId: null,
        editorUserId: null,
        bzObjMemberId: null,
        creationDate: null,
        finishedDate: null,
        release: null,
        releaseAuditContractId: null,
        groupId: null,
        insertTime: null,
        actualAmount: null,
        costCenterId: null,
        lastEdit: null,
        lookupId: null,
        name: null,
        note: null,
        number: null,
        positionId: null,
        priorityLookupId: null,
        productionDate: null,
        businessPartnerId: null,
        businessPartnerType: null,
        targetAmount: null,
        status: null,
        updateTime: null,
        article: {
          id: null,
          name: null,
          number: null,
          ressourceClassId: null,
        },
        businessPartner: {
          id: null,
          businessPartnerType: null,
          data: {
            customer: {
              id: null,
              name: null,
            },
            supplier: {
              id: null,
              name: null,
            },
          },
        },
        contact: {
          id: null,
          displayName: null,
        },
        editor: {
          id: null,
          displayName: null,
          shortName: null,
          phoneNumber: null,
          eMail: null,
        },
        creator: {
          id: null,
          displayName: null,
          shortName: null,
          phoneNumber: null,
          eMail: null,
        },
        projectId: null,
        project: {
          id: null,
          name: null,
        },
        productionContractGroup: {
          id: null,
          name: null,
        },
      };
      return productionContractData;
    } else {
      const statusProps =
        entity === Entities.invoice
          ? {
              ra: null,
              paid: null,
            }
          : { isClosed: null };
      const signatureProps =
        entity === Entities.deliveryNote
          ? {
              signature: {
                id: null,
                fileInfos: {
                  downloadUrl: null,
                },
              },
            }
          : {};

      const contractData =
        entity === Entities.contract
          ? {
              contractDate: null,
              contractNumber: null,
              paymentDeadline: null,
            }
          : {};

      const commonProps =
        entity === Entities.inboundDeliveryNote
          ? {
              language: {
                id: null,
                name: null,
              },
            }
          : {
              additionalAddress1Text: null,
              value: null,
              languageId: null,
              language: {
                id: null,
                name: null,
              },
            };
      const commonPropsPositions =
        entity === Entities.inboundDeliveryNote
          ? {}
          : {
              insteadOfTotalPrice: null,
            };
      const requestErpData: ERPTypes<EMode.query> = {
        id: null,
        note: null,
        // verify  entityType === Entities.contract //--contractDate
        date: null,
        ...contractData,
        number: null,
        ...commonProps,
        user: {
          id: null,
          displayName: null,
          phoneNumber: null,
          eMail: null,
        },
        projectId: null,
        project: {
          id: null,
          name: null,
        },
        ...signatureProps,
        frozen: null,
        ...statusProps,
        ...(entity === Entities.invoice && invoiceFields),
        headText: null,
        footerText: null,
        footerTextConverted: null,
        headerTextConverted: null,
        rtfHeadText: null,
        rtfFooterText: null,
        currency: { id: null, name: null },
        priceGroup: {
          id: null,
          isGrossPrice: null,
          standard: null,
        },
        positionList: [
          {
            id: null,
            vat: null,
            totalPriceNet: null,
            positionKind: null,
            number: null,
            amount: null,
            singlePriceNet: null,
            longtext: null,
            shortText: null,
            unit: null,
            internalText: null,
            ...commonPropsPositions,
          },
        ],
        businessPartnerId: null,
        businessPartnerType: null,
        contactId: null,
        businessPartnerContactCombined: {
          businessPartner: {
            id: null,
            data: {
              customer: {
                id: null,
                name: null,
                number: null,
              },
              supplier: {
                id: null,
                name: null,
                number: null,
              },
            },
          },
          contact: {
            id: null,
            displayName: null,
          },
        },
        archivePdf: [
          {
            id: null,
            downloadUrl: null,
            mimeType: null,
            xML: null,
            fileInfos: {
              fileEntityFilename: null,
              downloadUrl: null,
              previewUrl: null,
              previewMimeType: null,
              downloadMimeType: null,
              fileServiceProviderInfos: {
                id: null,
                fileName: null,
                mimeType: null,
                fspUrl: null,
              },
            },
          },
        ],
        businessPartnerText: null,
        erpAttachmentList: [
          {
            id: null,
            fileName: null,
            fileInfos: {
              fileSize: null,
              downloadUrl: null,
              fileEntityFilename: null,
              previewUrl: null,
              previewMimeType: null,
              downloadMimeType: null,
              fileRequestType: null,
              fileServiceProviderInfos: {
                customUrl: null,
                exists: null,
                fileName: null,
                fspUrl: null,
                id: null,
                key: null,
                mimeType: null,
                size: null,
                thumbnail: null,
              },
            },
          },
        ],
      };
      return requestErpData;
    }
  }, [entity]);

  const previewDataResponse = useEntityPreviewDataRequest<
    ERPTypes & ReViewModel & RaViewModel & ProductionContract
  >(ids, entity, requestEntityData);

  const entitiesData = previewDataResponse.data;
  const fileList = useMemo<
    {
      fileInfos?: FileEntity;
      mimeType?: string;
      id?: number;
      parentId?: number;
    }[]
  >(() => {
    if (entity === Entities.reViewModel) {
      let receipts = (entitiesData?.[0] as ReViewModel)?.receipts;
      if (receipts && receipts.length > 1) {
        receipts = [...receipts];
        receipts.sort((a, b) => {
          return !a.parentId && b.parentId
            ? -1
            : a.parentId && !b.parentId
            ? 1
            : 0;
        });
      }

      return receipts;
    }

    if (entity === Entities.raViewModel) {
      return (entitiesData?.[0] as RaViewModel)?.invoice?.archivePdf;
    }

    return (entitiesData?.[0] as ERPTypes)?.archivePdf;
  }, [entitiesData, entity]);

  return { ...previewDataResponse, fileList, requestEntityData };
};
